import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSummary, selectDay, selectProject, selectToday } from './data/planner-data-service';
import ProjectView from './ui/projects/project-view';
import TodayView from './ui/today/today-view';
import {
    combineProjectData,
    findAndOpenTodoPath,
    getAllSectionTodos,
    getTodoFromCombinedProject,
    getTodoIdFromHash,
} from './domain/planner-provider';
import DayView from './ui/today/day-view';
import { ITodo, ListType } from './planner-interfaces';
import { showToast } from '../../common/domain/toast-provider';
import SummaryView from './ui/summary/summary-view';
import { usePlannerStore } from './data/planner-store';
import TodoSplitPopup from './ui/todo/todo-split/todo-split-popup';
import { openProject, openSection } from './domain/planner-storage';

const PlannerHome = () => {

    const navigate = useNavigate();

    const { project_id } = useParams();

    const setListType = usePlannerStore(state => state.setListType);
    const combinedProject = usePlannerStore(state => state.combinedProject);
    const setCombinedProject = usePlannerStore(state => state.setCombinedProject);
    const splitPopupTodo = usePlannerStore(state => state.splitPopupTodo);
    const setMultipleSelectTodos = usePlannerStore(state => state.setMultipleSelectTodos);

    const isTodayView = project_id === 'today' || project_id === undefined;
    const isDayView = project_id?.startsWith('day-');
    const isSummaryView = project_id === 'summary';

    useEffect(() => {
        if(isTodayView) {
            setListType(ListType.TodayView);
            return;
        }

        if(isDayView) {
            setListType(ListType.DayView);
            return;
        }

        if(isSummaryView) {
            setListType(ListType.SummaryView);
            return;
        }

        setListType(ListType.ProjectView);
    }, [isTodayView, isDayView, isSummaryView, setListType]);

    useEffect(() => {
        (async () => {

            setMultipleSelectTodos([]);

            if(isTodayView) {
                const todayResponse = await selectToday();
                if(!todayResponse) {
                    showToast('Get today data error.');
                    return;
                }

                setCombinedProject(combineProjectData(todayResponse));
                return;
            }

            if(isDayView) {
                const dayResponse = await selectDay(project_id);
                if(!dayResponse) {
                    showToast('Get day data error.');
                    return;
                }

                setCombinedProject(combineProjectData(dayResponse));
                return;
            }

            if(isSummaryView) {
                const dayResponse = await getSummary();
                if(!dayResponse) {
                    showToast('Get summary data error.');
                    return;
                }

                setCombinedProject(combineProjectData(dayResponse));
                return;
            }

            const projectResponse = await selectProject(Number(project_id) || 0);

            if(!projectResponse) {
                showToast('Get project data error.');
                return;
            }

            setCombinedProject(combineProjectData(projectResponse));
        })();
    }, [
        project_id, setCombinedProject,
        navigate, isDayView,
        isSummaryView, isTodayView,
        setMultipleSelectTodos,
    ]);

    /**
     * On page load, check if there is "_todo hash" tag.
     * And scroll to it if it exists.
     * Open all nested items on the way.
     * http://localhost:3000/planner/7#todo-8905
     * http://localhost:3000/planner/7#todo-8836
     */
    useEffect(() => {
        if(!combinedProject) return;

        const todo_id = getTodoIdFromHash(window.location.hash);
        if(!todo_id) return;

        history.replaceState(null, null, ' ');

        const $todo = document.getElementById(`todo-${ todo_id }`);
        if($todo) {
            $todo.scrollIntoView();
        }
        else{

            const todo = getTodoFromCombinedProject(todo_id, combinedProject);
            if(!todo) return;

            const projectIndex = combinedProject.projects.findIndex(proj => proj.project_id === todo.project_id);
            if(projectIndex === -1) return;

            const copy = { ...combinedProject };
            copy.projects = [...copy.projects];

            const copyProject = {...combinedProject.projects[projectIndex] };
            copy.projects[projectIndex] = copyProject;

            copyProject.isOpened = true;
            openProject(copyProject.project_id);

            const sectionIndex = copyProject.sections.findIndex(sect => sect.section.section_id === (todo.section_id ?? 0));
            if(sectionIndex === -1) return;

            copyProject.sections = [...copyProject.sections];
            const copySection = {...copyProject.sections[sectionIndex] };
            copyProject.sections[sectionIndex] = copySection;

            copySection.isOpened = true;
            openSection(copySection.section.section_id);

            const allSectionTodos: ITodo[] = getAllSectionTodos(copySection);

            findAndOpenTodoPath(allSectionTodos, todo);

            setCombinedProject(copy);

            window.setTimeout(() => {
                const $todo = document.getElementById(`todo-${ todo_id }`);
                if(!$todo) return;

                $todo.scrollIntoView();
            }, 10);
        }

    }, [combinedProject, setCombinedProject]);

    const onOutSideClick = () => {
        setMultipleSelectTodos([]);
    };

    return (
        <>
            <div
                className="flex flex-col flex-1 leading-7 h-screen relative z-50 bg-white w-screen"
                onClick={ onOutSideClick }>
                <div className="overflow-auto px-6 py-10 flex-1">
                    { isTodayView && <TodayView/> }

                    { isDayView && <DayView /> }

                    { isSummaryView && <SummaryView /> }

                    {
                        !isTodayView &&
                        !isDayView &&
                        !isSummaryView &&
                        combinedProject?.projects.map(project => {

                            return (
                                <ProjectView
                                    key={`project-${ project.project_id }`}
                                    project={ project}
                                />
                            )
                        })
                    }
                </div>
            </div>

            {
                splitPopupTodo &&
                <TodoSplitPopup />
            }
        </>
    )
};

export default PlannerHome;